import { computedFrom, BindingEngine } from "aurelia-binding";
import { ListModel, ListItemModel, DeleteListRequest } from "../../../../../models/lists/list.models";
import { ListService } from "../../../../../services/modules/list-client.service";
import { StepModel } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { transient, autoinject } from "aurelia-framework";
import { ActiveMenuItemsService } from "../../../../../services/basics/active-menu-items.service";
import { Reflection } from "../../../../../utilities/reflection";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { BaseEntity } from "../../../modules/base-entity";
import { EntityItem } from "../../../modules/entity-item";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { BaseValidationResponse, ValidationResult } from "../../../../../models/validations/validations.models";
import { Guid } from "../../../../../utilities/guid";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { PscPerformance } from "./steps/psc-performance/psc-performance";
import { VesselListItems } from "../vessel-list-items/vessel-list-items";
import { CompanyListItems } from "../company-list-items/company-list-items";
import { ListAlertSettings } from "./steps/list-alert-settings";
import { ListHistoryTrail } from "./steps/list-history-trail";
import { MenuItem } from "../../../../components/menu/menu-item";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";

@autoinject()
@transient()
export class ListItems extends BaseEntity<ListModel> {

    entityItem: EntityItem;
    listItemCount: number;
    public isRightShipUser: boolean = false;
    public canEditProtectedList: boolean = false;
    public canEditProtectedListItem: boolean = false;
    public canAccessListAlerts: boolean = false;
    public canAccessListPSCPerformance: boolean = false;
    public canSeeListsHistoryTrail: boolean = false;
    public canAccessListsActions: boolean = false;
    public canImportListItems: boolean = false;
    public canEditLists: boolean = false;
    public canDeleteLists: boolean = false;
    public businessUnitId: number;
    public customerId: number;

    @computedFrom(Reflection.getPropertyPath<ListItems>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const prevInstruction = this.router.currentInstruction.previousInstruction;
        return prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');
    }

    @computedFrom('item')
    public get isCompanyListItems(): boolean {
        return this.item ? this.item.entityTypeId == EntityTypes.Company : false;
    }

    @computedFrom('item')
    public get isVesselListItems(): boolean {
        return this.item ? this.item.entityTypeId == EntityTypes.Vessel : false;
    }

    constructor(private listService: ListService, private activeMenuItemsService: ActiveMenuItemsService, router: Router, i18N: I18N,
        bindingEngine: BindingEngine, notificationService: NotificationClientService, eventAggregator: EventAggregator,
        validationsClientService: ValidationsClientService,
        private pscPerformance: PscPerformance, private vesselListItems: VesselListItems, private companyListItems: CompanyListItems,
        private listHistoryTrail: ListHistoryTrail,
        private listAlertSettings: ListAlertSettings, private dialogClient: DialogClientService,
        userClaimsService: UserClaimsService, private customerService: CommonCustomerService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
        pscPerformance.isAccessedFromLists = true;
        this.customerService.getBusinessUnitId().then(id => {
            this.businessUnitId = id;
        });
        this.customerService.getCustomerId().then(id => {
            this.customerId = id;
        });
        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        this.canEditProtectedList = userClaimsService.hasPermission(Claims.CanEditProtectedList);
        this.canEditProtectedListItem = userClaimsService.hasPermission(Claims.CanEditProtectedListItem);
        
        this.canAccessListAlerts = userClaimsService.hasPermission(Claims.CanAccessListAlerts);
        this.canAccessListPSCPerformance = userClaimsService.hasPermission(Claims.CanAccessListPSCPerformance);
        this.canSeeListsHistoryTrail = userClaimsService.hasPermission(Claims.CanSeeListsHistoryTrail);
        this.canAccessListsActions = userClaimsService.hasPermission(Claims.CanAccessListsActions);
        this.canImportListItems = userClaimsService.hasPermission(Claims.CanImportListItems); 
        this.canEditLists = userClaimsService.hasPermission(Claims.CanEditLists); 
        this.canDeleteLists = userClaimsService.hasPermission(Claims.CanDeleteLists); 
    }

    activate(params: any, navigationInstruction: any) {
        this.activeMenuItemsService.setActiveFromMenuItem('Lists');
        return super.activate(params, navigationInstruction);
    }


    public navigateBack() {
        this.router.navigateBack();
    }

    protected getEntityType(): EntityTypes {
        return this.item ? this.item.entityTypeId : null;
    }

    protected getItem(id: number): Promise<ListModel> {
        return new Promise<ListModel>((resolve, reject) => {
            this.listService.getList(id).then(list => {
                this.listItemCount = list?.listItemCount ?? 0;
                resolve(list.list);
            }, reject);
        });

    }

    attached() {
        super.attached();
        if (this.router.currentInstruction)
            this.router.currentInstruction.config.title = this.item.name;
    }

    protected getItemId(): number {
        return this.item ? this.item.listId : null;
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Lists;
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: (this.isCompanyListItems ? "Companies" : this.isVesselListItems ? "Vessels" : "List Items") + ` (${this.listItemCount})`,
                step: 1,
                vm: this.isCompanyListItems ? this.companyListItems : this.isVesselListItems ? this.vesselListItems : null,
                show: this.isCompanyListItems || this.isVesselListItems
            },
            {
                lazyLoad: true,
                title: 'PSC Performance',
                step: 2,
                vm: this.pscPerformance,
                show: this.canAccessListPSCPerformance
            },
            {
                lazyLoad: true,
                title: "Alert Settings",
                step: 3,
                vm: this.listAlertSettings,
                show: this.canAccessListAlerts
            },
            {
                lazyLoad: true,
                title: "History Trail",
                step: 4,
                vm: this.listHistoryTrail,
                show: this.canSeeListsHistoryTrail
            }];
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.ListItems;
    }

    protected initializeItemForCreate(): Promise<ListModel> | ListModel {
        const list: ListModel = {
            name: null,
            entityType: null,
            customerId: null,
            businessUnitId: null,
            description: null,
            entityTypeId: null,
            identifier: Guid.generateGuid(),
            listGroupId: null,
            listId: null,
            listItems: [],
            isPersonal: false,
            isInternal: false,
            isActive: false
        };

        setTimeout(() => {
            list.isActive = true;
        });

        return list;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        throw new Error("Method not implemented.");
    }

    getMenuItems(): MenuItem[] {
        return [
            {
                isVisible:                
                    (this.canImportListItems &&
                        (this.isRightShipUser || (this.businessUnitId === this.businessUnitId) || (this.item.customerId === this.customerId)))
                    && (this.canEditProtectedList || !this.item.isInternal),
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ImportExcelDialog, {
                        onComplete: () => {
                            // this.reload();
                            this.getEntity(this.item.listId);
                        },
                        entityTypeId: EntityTypes.ListItem,
                        filter: "list_" + this.item.listId,
                        explicitValues: [
                            {
                                property: Reflection.unCamelCase(Reflection.getPropertyName<ListItemModel>(x => x.listId), false),
                                value: this.item.listId
                            }
                        ]
                    }, true, false, false, 'right');
                },
                item: { text: "Import", import: true, export: false, history: false, edit: false, remove: false },
                classList: ''
            },
            {
                isVisible: true,
                click: () => {
                    this.listService.exportList(this.item.listId, this.item.name);
                },
                item: { text: "Export", import: false, export: true, history: false, edit: false, remove: false },
                classList: ''
            },
            {
                isVisible: true,
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ListImportsHistory, { listId: this.item.listId }, true, false, false, 'right');
                },
                item: { text: "Imports History", import: true, export: false, history: false, edit: false, remove: false },
                classList: ''
            },
            {
                isVisible: () =>
                    this.canEditLists &&
                    (this.isRightShipUser || this.item.businessUnitId === this.businessUnitId || this.item.customerId === this.customerId) &&
                    (!this.item.isInternal || this.canEditProtectedList),
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditListDialog, {
                        onComplete: () => {
                            // this.reload();
                            this.getEntity(this.item.listId);
                        },
                        id: this.item ? this.item.listId : null
                    }, true, false, false, 'right');
                },
                item: { text: "Edit", import: false, export: false, history: false, edit: true, remove: false },
                classList: ''
            },
            {
                isVisible: () =>                
                    this.canDeleteLists &&
                    (this.isRightShipUser || this.item.businessUnitId === this.businessUnitId || this.item.customerId === this.customerId) &&
                    (!this.item.isInternal || this.canEditProtectedList),
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ListDeleteDialog,
                        { id: this.item.listId, listName: this.item.name, deleteFunc: this.deleteList }, true, false, false, 'right');
                },
                item: { text: "Remove", import: false, export: false, history: false, edit: false, remove: true },
                classList: ''
            }
        ].filter(x => x.isVisible);
    }

    deleteList = (id: number, reason: string) => {
        return new Promise<void>((resolve, reject) => {
            const request: DeleteListRequest = {
                id: id,
                reason: reason
            }

            this.listService.deleteList(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                }
                else {
                    this.notificationService.show(this.i18N.tr('successfully-deleted-text'), "", NotificationType.Success, 5000);
                    //go back to lists page after delete list
                    this.goBack();
                    resolve();
                }
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                    validationResult: ValidationResult;
                } = typeof (x.response) === 'object'
                        ? x.response
                        : (x.response ? JSON.parse(x.response) : {});
                if (!response.exception || !response.details) {
                    this.notificationService.show(this.i18N.tr('failed-list-deletion-text'), "", NotificationType.Error, 5000);
                } else if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                reject();
            });
        })
    };
}
